import {ImageConfig} from '@mintdata/mint-components/components/image/ImageConfig';
import {ImageUtil} from '@mintdata/mint-components/components/image/ImageUtil';
import {CustomState} from '@mintdata/mint-components/components/image/model/CustomState';
import {ImageModel} from '@mintdata/mint-components/components/image/model/ImageModel';
import {GraphicRef, GraphicType, IGraphic} from '@mintdata/mint-kit/api/assets/Graphic';
import {UploadedImage} from '@mintdata/mint-kit/api/assets/UploadedImage';
import {
    BaseComponentWithStateContext,
    componentStateContextTypes
} from '@mintdata/mint-kit/api/components/BaseComponentWithStateContext';
import {Component} from '@mintdata/mint-kit/api/decorators/Component';
import {Event} from '@mintdata/mint-kit/api/decorators/Event';
import {Property} from '@mintdata/mint-kit/api/decorators/Property';
import {AnEvent} from '@mintdata/mint-kit/api/events/EventFn';
import {AFocusEvent, AMouseEvent} from '@mintdata/mint-kit/api/events/EventType';
import {TextVerticalAlign} from '@mintdata/mint-kit/api/models/TextVerticalAlign';
import {BaseProps} from '@mintdata/mint-kit/api/properties/BaseProps';
import {PropertyCategory} from '@mintdata/mint-kit/api/properties/PropertyCategory';
import {PropertyPanelTabId} from '@mintdata/mint-kit/api/properties/PropertyPanelTabId';
import {Spinner} from '@mintdata/mint-kit/impl/components/spinner/Spinner';
import {IPromiseCancelable} from '@mintdata/mint-kit/impl/util/PromiseUtil';
import bind from 'bind-decorator';

import * as classnames from 'classnames/bind';
import * as React from 'react';

const styles = require('@mintdata/mint-components/components/image/Image.less');
const cx = classnames.bind(styles);

export class ImageProps extends BaseProps
{
    @Property<ImageModel>({
        title: '',
        tip: '',
        category: PropertyCategory.Appearance,
        isRequired: false,
        typeConfig: ImageConfig,
        typeCtor: ImageModel
    })
    model?:ImageModel = ImageModel.default();

    @Event({isEnabled: true, title: 'onClick', tip: 'Click'})
    onClick?:AnEvent<AMouseEvent>;

    @Event({title: 'onDoubleClick', tip: 'Double click'})
    onDoubleClick?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseDown', tip: 'Mouse down'})
    onMouseDown?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseUp', tip: 'Mouse up'})
    onMouseUp?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseMove', tip: 'Mouse move'})
    onMouseMove?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseOver', tip: 'Mouse over'})
    onMouseOver?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseOut', tip: 'Mouse out'})
    onMouseOut?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseEnter', tip: 'Mouse enter'})
    onMouseEnter?:AnEvent<AMouseEvent>;

    @Event({title: 'onMouseLeave', tip: 'Mouse leave'})
    onMouseLeave?:AnEvent<AMouseEvent>;

    @Event({title: 'onFocused', tip: 'Focused'})
    onFocused?:AnEvent<AFocusEvent>;

    @Event({title: 'onLostFocus', tip: 'Lost focus'})
    onLostFocus?:AnEvent<AFocusEvent>;
}

interface IImageState
{
    graphic:{[key:string]:IGraphic};
    isGraphicLoading:{[key:string]:boolean};

    isHovered:boolean;
    isActive:boolean;
}

@Component<ImageProps>(
    {
        name: 'Image',
        tip: 'Image',
        props: ImageProps,
        width: 210,
        height: 150
    })
export class Image extends BaseComponentWithStateContext<ImageProps, IImageState>
{
    private _graphicPromiseCancelable:{[key:string]:IPromiseCancelable<IGraphic>} = {
        [PropertyPanelTabId.Default]: null,
        [PropertyPanelTabId.Hover]: null,
        [PropertyPanelTabId.Active]: null
    };

    public static readonly childContextTypes = componentStateContextTypes;

    public constructor(props:ImageProps)
    {
        super(props);

        this.state = {
            graphic: {
                [PropertyPanelTabId.Default]: null,
                [PropertyPanelTabId.Hover]: null,
                [PropertyPanelTabId.Active]: null
            },
            isGraphicLoading: {
                [PropertyPanelTabId.Default]: false,
                [PropertyPanelTabId.Hover]: false,
                [PropertyPanelTabId.Active]: false
            },
            isHovered: false,
            isActive: false
        };
    }

    public componentDidMount():void
    {
        this._loadGraphic(PropertyPanelTabId.Default);
        this._loadGraphic(PropertyPanelTabId.Hover);
        this._loadGraphic(PropertyPanelTabId.Active);
    }

    public componentDidUpdate(prevProps:ImageProps):void
    {
        const {model} = this.props;
        const {model: prevModel} = prevProps;

        const graphicName:string = this._getGraphicName(model, PropertyPanelTabId.Default);
        const prevGraphicName:string = this._getGraphicName(prevModel, PropertyPanelTabId.Default);

        const hoverGraphicName:string = this._getGraphicName(model, PropertyPanelTabId.Hover);
        const prevHoverGraphicName:string = this._getGraphicName(prevModel, PropertyPanelTabId.Hover);

        const activeGraphicName:string = this._getGraphicName(model, PropertyPanelTabId.Active);
        const prevActiveGraphicName:string = this._getGraphicName(prevModel, PropertyPanelTabId.Active);

        if( graphicName !== prevGraphicName )
            this._loadGraphic(PropertyPanelTabId.Default);

        if( hoverGraphicName !== prevHoverGraphicName )
            this._loadGraphic(PropertyPanelTabId.Hover);

        if( activeGraphicName !== prevActiveGraphicName )
            this._loadGraphic(PropertyPanelTabId.Active);
    }

    public componentWillUnmount()
    {
        if( this._graphicPromiseCancelable[PropertyPanelTabId.Default] )
            this._graphicPromiseCancelable[PropertyPanelTabId.Default].cancel();

        if( this._graphicPromiseCancelable[PropertyPanelTabId.Hover] )
            this._graphicPromiseCancelable[PropertyPanelTabId.Hover].cancel();

        if( this._graphicPromiseCancelable[PropertyPanelTabId.Active] )
            this._graphicPromiseCancelable[PropertyPanelTabId.Active].cancel();
    }

    private async _loadGraphic(tabId:PropertyPanelTabId):Promise<void>
    {
        const {assets, model} = this.props;

        const {name, type} = this._getGraphicRef(model, tabId);

        // treat as url
        if( !type )
            return;

        this.setState((prevState:IImageState) => ({
            isGraphicLoading: {
                ...prevState.isGraphicLoading,
                [tabId]: true
            }
        }));

        try
        {
            if( this._graphicPromiseCancelable[tabId] )
                this._graphicPromiseCancelable[tabId].cancel();

            this._graphicPromiseCancelable[tabId] = assets.graphics.getGraphic(type, name);

            const graphic:IGraphic = await this._graphicPromiseCancelable[tabId].promise;

            this.setState((prevState:IImageState) => ({
                graphic: {
                    ...prevState.graphic,
                    [tabId]: graphic
                },
                isGraphicLoading: {
                    ...prevState.isGraphicLoading,
                    [tabId]: false
                }
            }));
        }
        catch( err )
        {
            if( !err.isCanceled )
            {
                console.error(err);

                this.setState((prevState:IImageState) => ({
                    graphic: {
                        ...prevState.graphic,
                        [tabId]: null
                    },
                    isGraphicLoading: {
                        ...prevState.isGraphicLoading,
                        [tabId]: false
                    }
                }));
            }
        }
    }

    private _getModelUrl(model:ImageModel, tabId:PropertyPanelTabId = PropertyPanelTabId.Default):string
    {
        if( tabId === PropertyPanelTabId.Default )
            return model.url.toString();

        if( tabId === PropertyPanelTabId.Hover )
            return model.hoverState.url.toString();

        if( tabId === PropertyPanelTabId.Active )
            return model.activeState.url.toString();
    }

    private _getGraphicRef(model:ImageModel, tabId:PropertyPanelTabId = PropertyPanelTabId.Default):GraphicRef
    {
        return GraphicRef.fromString(
            this._getModelUrl(model, tabId),
            this.props.assets.graphics.getUploadedImageByUrl
        );
    }

    private _getGraphicName(model:ImageModel, tabId:PropertyPanelTabId = PropertyPanelTabId.Default):string
    {
        const graphicRef:GraphicRef = this._getGraphicRef(model, tabId);

        if( graphicRef )
            return graphicRef.name;

        return '';
    }

    @bind
    private _onClick(event:React.MouseEvent<HTMLElement>):void
    {
        const {onClick} = this.props;

        if( onClick )
            onClick(new AMouseEvent(event));
    }

    @bind
    private _onDoubleClick(event:React.MouseEvent<HTMLElement>):void
    {
        const {onDoubleClick} = this.props;

        if( onDoubleClick )
            onDoubleClick(new AMouseEvent(event));
    }

    @bind
    private _onMouseDown(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseDown, model: {activeState}, isDraggable} = this.props;

        if( onMouseDown )
            onMouseDown(new AMouseEvent(event));

        if( isDraggable )
            return;

        this.setState({
            isActive: true
        });

        if( activeState.isEnabled.value && !this.context.isParentActiveEventPropagationEnabled )
            event.stopPropagation();
    }

    @bind
    private _onMouseUp(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseUp} = this.props;

        this.setState({
            isActive: false
        });

        if( onMouseUp )
            onMouseUp(new AMouseEvent(event));
    }

    @bind
    private _onMouseMove(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseMove} = this.props;

        if( onMouseMove )
            onMouseMove(new AMouseEvent(event));
    }

    @bind
    private _onMouseOver(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseOver} = this.props;

        if( onMouseOver )
            onMouseOver(new AMouseEvent(event));
    }

    @bind
    private _onMouseOut(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseOut} = this.props;

        if( onMouseOut )
            onMouseOut(new AMouseEvent(event));
    }

    @bind
    private _onMouseEnter(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseEnter} = this.props;

        this.setState({
            isHovered: true
        });

        if( onMouseEnter )
            onMouseEnter(new AMouseEvent(event));
    }

    @bind
    private _onMouseLeave(event:React.MouseEvent<HTMLElement>):void
    {
        const {onMouseLeave} = this.props;

        this.setState({
            isHovered: false,
            isActive: false
        });

        if( onMouseLeave )
            onMouseLeave(new AMouseEvent(event));
    }

    @bind
    private _onFocused(event:React.FocusEvent<HTMLElement>):void
    {
        const {onFocused} = this.props;

        if( onFocused )
            onFocused(new AFocusEvent(event));
    }

    @bind
    private _onLostFocus(event:React.FocusEvent<HTMLElement>):void
    {
        const {onLostFocus} = this.props;

        if( onLostFocus )
            onLostFocus(new AFocusEvent(event));
    }

    public render():JSX.Element
    {
        const {model} = this.props;
        const {
            background,
            border,
            boxShadow,
            padding,
            hoverState,
            activeState
        } = model;

        let type:GraphicType = this._getGraphicRef(model, PropertyPanelTabId.Default).type;
        let url:string = this._getModelUrl(model, PropertyPanelTabId.Default);

        let style:React.CSSProperties = {
            ...background.toCss(),
            ...border.toCSS(),
            paddingTop: padding && padding.top && padding.top.toString(),
            paddingRight: padding && padding.right && padding.right.toString(),
            paddingBottom: padding && padding.bottom && padding.bottom.toString(),
            paddingLeft: padding && padding.left && padding.left.toString(),
            boxShadow: boxShadow.toCssString()
        };

        if(
            (this.state.isHovered || this.context.isParentHovered || this.props.propertyPanelSelectedTabId === PropertyPanelTabId.Hover)
            && hoverState.isEnabled.value
        )
        {
            type = this._getGraphicRef(model, PropertyPanelTabId.Hover).type;
            url = this._getModelUrl(model, PropertyPanelTabId.Hover);
            style = {...style, ...this._getComponentStateStyle(hoverState)};
        }

        if(
            (this.state.isActive || this.props.propertyPanelSelectedTabId === PropertyPanelTabId.Active)
            && activeState.isEnabled.value
        )
        {
            type = this._getGraphicRef(model, PropertyPanelTabId.Active).type;
            url = this._getModelUrl(model, PropertyPanelTabId.Active);
            style = {...style, ...this._getComponentStateStyle(activeState)};
        }

        return (
            <div
                style={style}
                onClick={this._onClick}
                onDoubleClick={this._onDoubleClick}
                onMouseDown={this._onMouseDown}
                onMouseUp={this._onMouseUp}
                onMouseMove={this._onMouseMove}
                onMouseOver={this._onMouseOver}
                onMouseOut={this._onMouseOut}
                onMouseEnter={this._onMouseEnter}
                onMouseLeave={this._onMouseLeave}
                onFocus={this._onFocused}
                onBlur={this._onLostFocus}
            >
                {
                    type ? (
                        this._renderGraphic()
                    ) : (
                        this._renderUrl(url)
                    )
                }
            </div>
        );
    }

    private _renderGraphic():JSX.Element
    {
        const {
            color1,
            color2,
            hoverState,
            activeState
        } = this.props.model;

        let graphic:IGraphic = this.state.graphic[PropertyPanelTabId.Default];
        let isGraphicLoading:boolean = this.state.isGraphicLoading[PropertyPanelTabId.Default];

        let graphicClassName:string = graphic && ImageUtil.getGraphicClassName(graphic.getType(), color1, color2);

        if(
            (this.state.isHovered || this.context.isParentHovered || this.props.propertyPanelSelectedTabId === PropertyPanelTabId.Hover)
            && hoverState.isEnabled.value
        )
        {
            graphic = this.state.graphic[PropertyPanelTabId.Hover];
            isGraphicLoading = this.state.isGraphicLoading[PropertyPanelTabId.Hover];

            graphicClassName = graphic && ImageUtil.getGraphicClassName(graphic.getType(), hoverState.color1, hoverState.color2);
        }

        if(
            (this.state.isActive || this.props.propertyPanelSelectedTabId === PropertyPanelTabId.Active)
            && activeState.isEnabled.value
        )
        {
            graphic = this.state.graphic[PropertyPanelTabId.Active];
            isGraphicLoading = this.state.isGraphicLoading[PropertyPanelTabId.Active];

            graphicClassName = graphic && ImageUtil.getGraphicClassName(graphic.getType(), activeState.color1, activeState.color2);
        }

        let result:JSX.Element = null;

        if( graphic )
        {
            switch( graphic.getType() )
            {
                case GraphicType.Illustration1:
                case GraphicType.Illustration2:
                {
                    result = (
                        <div
                            className={cx('graphic', graphicClassName)}
                            dangerouslySetInnerHTML={{__html: graphic ? (graphic as any).svg : null}}
                        />
                    );

                    break;
                }

                case GraphicType.UploadedImage:
                {
                    result = this._renderUrl((graphic as UploadedImage).url);
                    break;
                }

                case GraphicType.Photo:
                    break;
            }
        }

        if( isGraphicLoading )
            return (
                <Spinner
                    wrapperClassName={cx('spinnerWrapper')}
                    className={cx('spinner')}
                />
            );

        return result;
    }

    private _renderUrl(url:string):JSX.Element
    {
        const {
            model: {
                background,
                backgroundSize,
                horizontalAlign,
                verticalAlign,
                hoverState,
                activeState
            }
        } = this.props;

        let styleInner:React.CSSProperties = {
            width: '100%',
            height: '100%',
            borderRadius: 'inherit',
            opacity: background.solidColor.color.alpha.value,
            backgroundImage: url ? `url(${url})` : `initial`,
            backgroundRepeat: url ? `no-repeat` : `initial`,
            backgroundPosition: `${horizontalAlign.toString()} ${TextVerticalAlign.toCssBackgroundPositionString(verticalAlign)}`,
            backgroundSize: backgroundSize.value
        };

        if(
            (this.state.isHovered || this.context.isParentHovered || this.props.propertyPanelSelectedTabId === PropertyPanelTabId.Hover)
            && hoverState.isEnabled.value
        )
            styleInner = {...styleInner, ...this._getComponentStateInnerStyle(hoverState)};

        if(
            (this.state.isActive || this.props.propertyPanelSelectedTabId === PropertyPanelTabId.Active)
            && activeState.isEnabled.value
        )
            styleInner = {...styleInner, ...this._getComponentStateInnerStyle(activeState)};

        return (
            <div style={styleInner}>
                <img
                    style={{visibility: 'hidden', maxWidth: '100%', maxHeight: '100%'}}
                    src={
                        url
                            ? url.startsWith('data:')
                            ? `url(${url})`
                            : url
                            : ``
                    }
                />
            </div>
        );
    }

    private _getComponentStateStyle(state:CustomState):React.CSSProperties
    {
        const {background, border, boxShadow} = state;

        return {
            ...background.toCss(),
            ...border.toCSS(),
            boxShadow: boxShadow.toCssString()
        };
    }

    private _getComponentStateInnerStyle(state:CustomState):React.CSSProperties
    {
        const {background, backgroundSize, horizontalAlign, verticalAlign} = state;

        return {
            opacity: background.solidColor.color.alpha.value,
            backgroundPosition: `${horizontalAlign.toString()} ${TextVerticalAlign.toCssBackgroundPositionString(verticalAlign)}`,
            backgroundSize: backgroundSize.value
        };
    }
}
